// @ts-strict-ignore
import React, { ChangeEvent, KeyboardEvent, useMemo } from 'react'
import { NumericInput } from '@blueprintjs/core'
import { HTMLSelect } from '../../../components/ui/html-select'
import { DateInput } from '@blueprintjs/datetime'
import {
    differenceInCalendarDays,
    differenceInCalendarMonths,
    format,
    parse,
    subDays,
    subMonths,
} from 'date-fns'
import { DateFilter, DateFilterValue, DateOperator, FilterValue } from '../../../constants/filter'

// Value is what will be used internally
// Label is what will be displayed to the user
interface OperatorSelect {
    value: DateOperator
    label: string
}

const defaultOperators: OperatorSelect[] = [
    { value: DateOperator.EqualTo, label: 'is equal to' },
    { value: DateOperator.Between, label: 'is between' },
    { value: DateOperator.IsNull, label: 'is null' },
    { value: DateOperator.LessThan, label: 'is before' },
    { value: DateOperator.LessThanOrEqualTo, label: 'is on or before' },
    { value: DateOperator.GreaterThan, label: 'is after' },
    { value: DateOperator.GreaterThanOrEqualTo, label: 'is on or after' },
    { value: DateOperator.InTheLast, label: 'is in the last' },
]

const allowedOperators: OperatorSelect[] = [...defaultOperators]

type FilterValueObject = {
    [DateOperator.InTheLast]?: string
    [DateOperator.GreaterThan]?: string
    [DateOperator.GreaterThanOrEqualTo]?: string
    [DateOperator.LessThan]?: string
    [DateOperator.LessThanOrEqualTo]?: string
    [DateOperator.EqualTo]?: string
    [DateOperator.Between]?: string
    period?: 'days' | 'months'
}

interface FilterDateProps {
    filter: DateFilter
    data: DateFilterValue
    onChange: (v: FilterValue) => any
    apply?: () => any
}

class DateFilterValueHelper {
    value: DateFilterValue
    onChange: Function

    constructor(value: DateFilterValue, onChange: (payload: object) => Function) {
        this.value = value
        this.onChange = onChange
    }

    isNumber(): boolean {
        const v = this.getValue()
        return !isNaN(v)
    }

    getValue(): any {
        if (typeof this.value === 'object') {
            return this.value[Object.keys(this.value)[0]]
        }
        return this.value
    }

    getPeriod(): 'days' | 'months' {
        return this.value?.period ?? 'days'
    }

    getOperator(): DateOperator {
        if (typeof this.value === 'object') {
            const keys = Object.keys(this.value)

            if (keys.length > 1) {
                if (keys.includes('last')) {
                    return DateOperator.InTheLast
                }
                return DateOperator.Between
            } else {
                return Object.keys(this.value)[0] as DateOperator
            }
        }

        return DateOperator.EqualTo
    }

    setValue(key: DateOperator, value: any): void {
        this.onChange({
            ...this.value,
            [key]: value,
        })
    }

    setPeriod(period: 'days' | 'months'): void {
        this.onChange({
            ...this.value,
            period: period,
        })
    }
}

export const FilterDate = ({ filter, data, onChange, apply }: FilterDateProps) => {
    const helper = new DateFilterValueHelper(data, onChange)
    const value = helper.getValue()
    const operator = helper.getOperator()
    const period = helper.getPeriod()

    if (operator == 'last' && helper.isNumber()) {
        let res: Date
        switch (period) {
            case 'months':
                res = subMonths(new Date(), value)
                break
            case 'days':
            default:
                res = subDays(new Date(), value)
                break
        }

        helper.setValue(operator, format(res, 'yyyy-MM-dd').toString())
    }
    const options = filter.operators
        ? allowedOperators.filter((o) => filter.operators.indexOf(o.value) !== -1)
        : defaultOperators

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && apply) {
            apply()
        }
    }

    const handleDirty = (v: DateFilterValue) => {
        let payload
        if (operator == DateOperator.InTheLast) {
            let res: Date = new Date()
            if (period == 'days') {
                res = subDays(new Date(), Number(v))
            } else if (period == 'months') {
                res = subMonths(new Date(), Number(v))
            }

            payload = {
                [operator]: format(res, 'yyyy-MM-dd').toString(),
                period: period,
            }
        } else if (operator == DateOperator.Between) {
            const keys = Object.keys(v)
            keys.forEach((key) => {
                let e
                if (v[key] instanceof Date) {
                    e = v[key]
                } else {
                    if (v[key] == 0) {
                        e = new Date()
                    } else {
                        e = parse(v[key], 'yyyy-MM-dd', new Date())
                    }
                }
                v[key] = format(e, 'yyyy-MM-dd').toString()
            })

            payload = v
        } else if (
            operator == DateOperator.LessThan ||
            operator == DateOperator.LessThanOrEqualTo ||
            operator == DateOperator.GreaterThan ||
            operator == DateOperator.GreaterThanOrEqualTo ||
            operator == DateOperator.EqualTo
        ) {
            const e = v as Date
            const r = format(e, 'yyyy-MM-dd').toString()

            if (operator == DateOperator.EqualTo) {
                payload = r
            } else {
                payload = {
                    [operator]: r,
                }
            }
        }

        onChange(payload)
    }

    const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const selectedType = e.target.value

        let payload: DateFilterValue
        if (selectedType === DateOperator.Between) {
            payload = {
                [DateOperator.GreaterThanOrEqualTo]: value,
                [DateOperator.LessThanOrEqualTo]: value,
            }
        } else {
            payload = {
                [selectedType]: value,
            }
        }

        onChange(payload)
    }

    return (
        <div className="mt-2">
            <HTMLSelect options={options} onChange={handleTypeChange} value={operator} />
            <br />
            <div onKeyDown={handleKeyDown}>
                <FilterDateInput
                    operator={operator}
                    data={data}
                    value={value}
                    onValueChange={handleDirty}
                    period={period}
                    setPeriod={(period) => helper.setPeriod(period)}
                />
            </div>
        </div>
    )
}

interface FilterDateInputProps {
    operator: DateOperator
    value: any
    data: FilterValueObject
    onValueChange: (FilterValue) => any
    onKeyDown?: (KeyboardEvent) => any
    period: 'days' | 'months'
    setPeriod: (string) => any
}

const FilterDateInput = ({
    operator,
    data,
    value,
    onValueChange,
    onKeyDown,
    period,
    setPeriod,
}: FilterDateInputProps): JSX.Element | null => {
    if (operator === DateOperator.IsNull) {
        return null
    }

    switch (operator) {
        case DateOperator.Between:
            const resolvePayload = (type: DateOperator) => (v: string) => {
                onValueChange({
                    ...data,
                    [type]: v,
                })
            }
            return (
                <div className="d-flex">
                    <FilterDateRange
                        value={data['>=']}
                        onValueChange={(v) => resolvePayload(DateOperator.GreaterThanOrEqualTo)(v)}
                    />
                    <FilterDateRange
                        value={data['<=']}
                        onValueChange={(v) => resolvePayload(DateOperator.LessThanOrEqualTo)(v)}
                    />
                </div>
            )
        case DateOperator.InTheLast:
            return (
                <FilterDays
                    setPeriod={setPeriod}
                    period={period}
                    value={value}
                    data={data}
                    onValueChange={onValueChange}
                    onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
                />
            )
        case DateOperator.EqualTo:
        default:
            return <FilterDateRange value={data} onValueChange={onValueChange} />
    }
}

const FilterDays = ({
    onValueChange,
    data,
    value,
    period,
    setPeriod,
    onKeyDown,
}: {
    onValueChange: (FilterValue) => any
    value: any
    data: FilterValueObject
    period: 'days' | 'months'
    setPeriod: (string) => any
    onKeyDown: (string) => any
}) => {
    const periodOptions = ['days', 'months']
    const periodOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (!periodOptions.includes(e.target.value)) {
            console.error(e.target.value + ' not supported')
            return
        }

        // Reset the input to 0
        // ex. 250 days => [type switched] => 0 months
        onValueChange(0)
        setPeriod(e.target.value)
    }

    const displayValue = useMemo(() => {
        if (data === undefined) {
            return 0
        }
        const z = parse(value, 'yyyy-MM-dd', new Date())
        let v: number
        if (period == 'days') {
            v = differenceInCalendarDays(z, new Date())
        } else if (period == 'months') {
            v = differenceInCalendarMonths(z, new Date())
        }

        return Math.abs(v)
    }, [data, period])

    return (
        <div className="d-flex">
            <NumericInput
                style={{ maxWidth: 100 }}
                onValueChange={onValueChange}
                value={displayValue}
                onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : null)}
            />
            <HTMLSelect options={periodOptions} onChange={periodOnChange} value={period} />
        </div>
    )
}

const FilterDateRange = ({
    onValueChange,
    value,
}: {
    onValueChange: (FilterValue) => any
    value: FilterValueObject | string
}) => {
    const props = {
        formatDate: (date: Date) => date.toLocaleDateString(),
        parseDate: (date: string) => new Date(date),
        placeholder: 'YYYY/MM/dd',
    }
    const inputProps = {
        style: {
            maxWidth: 100,
            borderRadius: 5,
        },
    }

    if (typeof value == 'object') {
        const keys = Object.keys(value)
        value = value[keys[0]]
    }
    const parsedValue = parse(value as string, 'yyyy-MM-dd', new Date())

    return (
        <div>
            <DateInput
                inputProps={inputProps}
                {...props}
                onChange={onValueChange}
                value={parsedValue}
            />
        </div>
    )
}
